import React from 'react';
import {Box, createStyles, Grid, makeStyles, Tooltip} from '@material-ui/core';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import {SwitchCustom, TextBox} from '../../../Core/UI';
import {Parameter} from '../../../Core/mobx/ParameterStore/ParameterStore';
import {observer} from 'mobx-react-lite';
import {CategoryCard} from '../CategoryCard';
import {Approval} from '../../../Core/mobx/ParameterStore/Approval';
import {SelectInputElement} from '../../PreQualification/Components/SelectInputElement';
import {fieldOptions} from '../../../Core/mobx/ParameterStore/ApprovalXref';

type BeneficiariesProps = {
    valueChanged: (p?: Parameter, value?: any) => void;
    parameters: Approval;
    setCategory: (entry: string) => void;
    index: number;
};
const useStyles = makeStyles(() =>
    createStyles({
        formControl: {
            marginTop: 8,
            width: '100%',
        },
        tooltip: {
            maxWidth: 500,
            width: '100%',
            background: '#fff',
            border: '1px solid gray',
            borderRadius: '10px',
            padding: '0 10px',
            margin: 0,
            color: '#444',
            fontFamily: 'FredokaRegular',
            fontSize: '18px',
            lineHeight: '24px',
            '& h1': {
                color: '#3f51b5',
                fontWeight: 600,
                fontSize: '26px',
                lineHeight: '26.px',
            },
        },
    }),
);
const Beneficiaries = observer(({valueChanged, parameters, setCategory, index}: BeneficiariesProps) => {
    const classes = useStyles();
    const required = (value?: string) => (
        <span>
            {value && value} <span style={{color: 'red'}}>*</span>
        </span>
    );

    const renderBeneficiaryFields = (index: number, contingent = false) => {
        const fieldType = contingent ? 'contBene' : 'bene';
        const bdayFieldPrefix = contingent ? 'contBday' : 'beneDay';
        return (
            <>
                <Grid item xs={4}>
                    <div
                        style={{
                            float: 'left',
                            width: '100%',
                            maxWidth: 'calc(100% - 24px)',
                        }}
                    >
                        <TextBox
                            id={`${fieldType}Info${index}`}
                            label={required(
                                contingent ? `Contingent Beneficiary Name ${index}` : `Beneficiary Name ${index}`,
                            )}
                            value={parameters[`${fieldType}Info${index}` as keyof typeof parameters]?.value}
                            onChange={(value) =>
                                valueChanged(parameters[`${fieldType}Info${index}` as keyof typeof parameters], value)
                            }
                        />
                    </div>
                    <Tooltip
                        arrow
                        leaveDelay={200}
                        placement="right"
                        classes={{tooltip: classes.tooltip}}
                        title={
                            <>
                                <h1>Don’t Forget!</h1>
                                <p>Please include middle initial and suffix if possible.</p>
                            </>
                        }
                    >
                        <InfoOutlinedIcon color="primary" />
                    </Tooltip>
                </Grid>
                <Grid item xs={4}>
                    <TextBox
                        id={`${fieldType}Percent${index}`}
                        label={required(contingent ? `Percentage (Must Equal 100)` : `Percentage`)}
                        type="number"
                        value={parameters[`${fieldType}Percent${index}` as keyof typeof parameters]?.value}
                        onChange={(value) =>
                            valueChanged(parameters[`${fieldType}Percent${index}` as keyof typeof parameters], value)
                        }
                    />
                </Grid>
                <Grid item xs={4}>
                    <SelectInputElement
                        param={{
                            label: 'Beneficiary Relationship',
                            name: `${fieldType}Relationship${index}`,
                            options: fieldOptions.beneRelationship.options,
                            ...parameters[`${fieldType}Relationship${index}` as keyof typeof parameters],
                        }}
                        required={required('')}
                        valueChanged={valueChanged}
                    />
                </Grid>
                <Grid item xs={4}>
                    <TextBox
                        id={`${fieldType}Month${index}`}
                        label={required('Birth Month')}
                        value={parameters[`${fieldType}Month${index}` as keyof typeof parameters]?.value}
                        onChange={(value) =>
                            valueChanged(parameters[`${fieldType}Month${index}` as keyof typeof parameters], value)
                        }
                    />
                </Grid>
                <Grid item xs={4}>
                    <TextBox
                        id={`${bdayFieldPrefix}${index}`}
                        label={required('Birth Day')}
                        type="number"
                        value={parameters[`${bdayFieldPrefix}${index}` as keyof typeof parameters]?.value}
                        onChange={(value) =>
                            valueChanged(parameters[`${bdayFieldPrefix}${index}` as keyof typeof parameters], value)
                        }
                    />
                </Grid>
                <Grid item xs={4}>
                    <TextBox
                        id={`${fieldType}Year${index}`}
                        label={required('Birth Year')}
                        type="number"
                        value={parameters[`${fieldType}Year${index}` as keyof typeof parameters]?.value}
                        onChange={(value) =>
                            valueChanged(parameters[`${fieldType}Year${index}` as keyof typeof parameters], value)
                        }
                    />
                </Grid>
                {contingent && (
                    <>
                        <Grid item xs={4}>
                            <TextBox
                                id={`${fieldType}Relationship${index}`}
                                label="Beneficiary Relationship (Deprecated)"
                                value={
                                    parameters[`${fieldType}Relationship${index}` as keyof typeof parameters]?.value ||
                                    'N/A'
                                }
                                disabled={true}
                                helperText="If there are values here please enter them into the new fields."
                            />
                        </Grid>
                        <Box style={{height: 12, width: '100%'}} />
                    </>
                )}
            </>
        );
    };

    const sumPercentages = (params: Approval, keys: string[]) => {
        return keys.reduce((total, key) => total + (+params[key as keyof typeof params]?.value || 0), 0);
    };

    const keysBene = ['benePercent1', 'benePercent2', 'benePercent3', 'benePercent4', 'benePercent5', 'benePercent6'];
    const keysContBene = [
        'contBenePercent1',
        'contBenePercent2',
        'contBenePercent3',
        'contBenePercent4',
        'contBenePercent5',
        'contBenePercent6',
    ];

    const showBeneTotalPercent = sumPercentages(parameters, keysBene) !== 100;
    const showContBeneTotalPercent = sumPercentages(parameters, keysContBene) !== 100;

    return (
        <CategoryCard title={'Beneficiaries'} setCategory={setCategory} index={index}>
            <Grid item xs={4}>
                <SelectInputElement
                    param={{
                        label: 'Number of Beneficiaries',
                        name: 'numberOfBenes',
                        options: fieldOptions.numberOfBenes.options,
                        ...parameters.numberOfBenes,
                    }}
                    required={required('')}
                    valueChanged={valueChanged}
                />
            </Grid>
            <Box style={{height: 12, width: '100%'}} />
            {[...Array(Number(parameters.numberOfBenes.value))].map((_, i) => renderBeneficiaryFields(i + 1, false))}
            {showBeneTotalPercent && parameters.numberOfBenes.value && (
                <Grid item xs={12}>
                    <div className={classes.formControl}>Beneficiary percentage does not add up to 100%</div>
                </Grid>
            )}
            <Grid item xs={12}>
                <SwitchCustom
                    param={{
                        name: 'contBene',
                        label: 'Contingent Beneficiaries',
                        ...parameters.contBene,
                    }}
                    required={required('')}
                    valueChanged={valueChanged}
                />
            </Grid>
            {parameters.contBene.value === true && (
                <>
                    <Grid item xs={4}>
                        <SelectInputElement
                            param={{
                                label: 'Number of Contingent Beneficiaries',
                                name: 'contNumberOfBenes',
                                options: fieldOptions.contNumberOfBenes.options,
                                ...parameters.contNumberOfBenes,
                            }}
                            required={required('')}
                            valueChanged={valueChanged}
                        />
                    </Grid>
                    <Box style={{height: 12, width: '100%'}} />
                    {[...Array(Number(parameters.contNumberOfBenes.value))].map((_, i) =>
                        renderBeneficiaryFields(i + 1, true),
                    )}
                    {showContBeneTotalPercent && (
                        <Grid item xs={12}>
                            <div className={classes.formControl}>
                                Contingent beneficiary percentage does not add up to 100%
                            </div>{' '}
                        </Grid>
                    )}
                </>
            )}
        </CategoryCard>
    );
});

export default Beneficiaries;
